import { CoinChip } from '../ui/components';
import { state, isPremiumActive, updateCoins } from '../store';
import { api, isHalted } from '../api/client';
import { t } from '../i18n';
import { safeStorage, safeSession } from '../utils/storage';
import { getNextMilestoneText } from '../utils/milestones';

// ── Global handler declarations ───────────────────────────────────────

declare global {
  interface Window {
    shareReferralTelegram?: () => void;
    copyReferralLink?: () => void;
    openEmailPopup?: () => void;
    closeEmailPopup?: () => void;
    submitEmailForm?: () => void;
    openChannelLink?: () => void;
    claimChannelReward?: () => void;
    claimPmTask?: () => void;
    __markInviteNeedsRefresh?: () => void;
  }
}

// ── Referral share ────────────────────────────────────────────────────

window.shareReferralTelegram = () => {
  const link = state.referralStats?.referral_link || '';
  const text = encodeURIComponent(t('invite_share_text'));
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${text}`;
  if ((window as any).Telegram?.WebApp?.openTelegramLink) {
    (window as any).Telegram.WebApp.openTelegramLink(shareUrl);
  } else {
    window.open(shareUrl, '_blank');
  }
};

window.copyReferralLink = () => {
  const link = state.referralStats?.referral_link || '';
  navigator.clipboard.writeText(link).then(() => {
    window.showToast(t('invite_copied'), 'success');
  }).catch(() => {
    window.showToast(t('error_copy'), 'error');
  });
};

// ── Email popup ───────────────────────────────────────────────────────

window.openEmailPopup = () => {
  if (document.getElementById('email-popup-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'email-popup-overlay';
  overlay.style.cssText = `
    position:fixed;inset:0;z-index:9000;
    background:rgba(7,6,15,0.85);backdrop-filter:blur(10px);
    display:flex;align-items:flex-end;justify-content:center;
  `;
  overlay.innerHTML = `
    <style>
      @keyframes slideUpEmail { from { transform: translateY(100%); } to { transform: translateY(0); } }
    </style>
    <div style="
      width:100%;max-width:430px;
      background:#131126;
      border-radius:24px 24px 0 0;
      border:1px solid rgba(255,255,255,0.08);
      padding:24px 20px 48px;
      animation:slideUpEmail 0.25s ease-out;
    ">
      <div style="width:36px;height:4px;background:rgba(255,255,255,0.15);border-radius:2px;margin:0 auto 20px;"></div>
      <div style="text-align:center;margin-bottom:20px;">
        <div style="font-size:44px;margin-bottom:8px;">📧</div>
        <div style="font-size:18px;font-weight:800;color:#fff;font-family:Inter,sans-serif;">${t('rewards_email_popup_title')}</div>
        <div style="font-size:13px;color:#7B79A0;margin-top:4px;font-weight:600;">${t('rewards_email_popup_sub')}</div>
      </div>
      <input id="email-input" type="email" autocomplete="email" placeholder="${t('rewards_email_placeholder')}"
        style="
          width:100%;box-sizing:border-box;
          padding:14px 16px;
          background:rgba(255,255,255,0.06);
          border:1.5px solid rgba(255,255,255,0.12);
          border-radius:14px;
          color:#fff;font-size:15px;
          font-family:Inter,sans-serif;
          outline:none;margin-bottom:6px;
        "
      />
      <div id="email-error" style="color:#FF5F7E;font-size:12px;font-weight:600;min-height:18px;margin-bottom:8px;padding:0 4px;"></div>
      <button id="email-submit-btn" data-action="submit-email-form" style="
        width:100%;padding:16px;
        background:linear-gradient(135deg,#2D6BFF,#00E5FF);
        border:none;border-radius:14px;
        color:#fff;font-size:16px;font-weight:800;
        font-family:Inter,sans-serif;cursor:pointer;
        box-shadow:0 0 24px rgba(45,107,255,0.4);
      ">
        ${t('rewards_email_submit')}
      </button>
      <button data-action="close-email-popup" style="
        width:100%;padding:12px;
        background:none;border:none;
        color:#7B79A0;font-size:14px;font-weight:600;
        font-family:Inter,sans-serif;cursor:pointer;margin-top:4px;
      ">
        ${t('cancel_btn')}
      </button>
    </div>
  `;

  // Close on backdrop tap
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) window.closeEmailPopup?.();
  });

  document.body.appendChild(overlay);
  setTimeout(() => (document.getElementById('email-input') as HTMLInputElement)?.focus(), 150);
};

window.closeEmailPopup = () => {
  document.getElementById('email-popup-overlay')?.remove();
};

window.submitEmailForm = async () => {
  const input = document.getElementById('email-input') as HTMLInputElement;
  const errorEl = document.getElementById('email-error');
  const btn = document.getElementById('email-submit-btn') as HTMLButtonElement;
  const email = input?.value?.trim() || '';

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    if (errorEl) errorEl.textContent = t('rewards_email_invalid');
    input?.focus();
    return;
  }
  if (errorEl) errorEl.textContent = '';

  if (btn) { btn.disabled = true; btn.textContent = '...'; }

  try {
    const res = await api.setEmail(email);
    if (res.ok) {
      const added = res.coins_added ?? 50;
      safeStorage.set('pg_email_claimed', '1');
      updateCoins((state.user?.coins ?? 0) + added, true);
      window.closeEmailPopup?.();
      window.showToast(t('rewards_task_claimed').replace('{n}', String(added)), 'success');
      window.renderApp();
    } else {
      if (errorEl) errorEl.textContent = t('rewards_task_error');
      if (btn) { btn.disabled = false; btn.textContent = t('rewards_email_submit'); }
    }
  } catch {
    if (errorEl) errorEl.textContent = t('rewards_task_error');
    if (btn) { btn.disabled = false; btn.textContent = t('rewards_email_submit'); }
  }
};

// ── Channel task ──────────────────────────────────────────────────────

window.openChannelLink = () => {
  safeStorage.set('pg_channel_visited', '1');
  const tg = (window as any).Telegram?.WebApp;
  const channelUrl = 'https://t.me/partygamesapp';
  if (tg?.openTelegramLink) {
    tg.openTelegramLink(channelUrl);
  } else {
    window.open(channelUrl, '_blank');
  }
  // Show "Claim" button after returning
  setTimeout(() => window.renderApp(), 1200);
};

window.claimChannelReward = async () => {
  try {
    const res = await api.claimTask('channel_sub');
    if (res.ok) {
      const added = res.coins_added ?? 50;
      safeStorage.set('pg_channel_claimed', '1');
      updateCoins((state.user?.coins ?? 0) + added, true);
      window.showToast(t('rewards_task_claimed').replace('{n}', String(added)), 'success');
      window.renderApp();
    } else {
      const msg = res.reason === 'not_subscribed'
        ? t('rewards_channel_title')
        : t('rewards_task_error');
      window.showToast(msg, 'error');
    }
  } catch {
    window.showToast(t('rewards_task_error'), 'error');
  }
};

// ── PM task ───────────────────────────────────────────────────────────
// Flow:
//  1. User taps card → requestWriteAccess → granted
//  2. We set localStorage "pg_pm_granted" + sessionStorage "pg_pm_this_session"
//     and re-render → card shows "Close & Reopen" state
//  3. User closes app (TG WebApp fully reloads on reopen → sessionStorage resets)
//  4. On reopen: localStorage "pg_pm_granted" is present but sessionStorage is gone
//     → card shows "Claim" button
//  5. auth.ts already updated allows_write_to_pm=1 on this reopen → backend succeeds

window.claimPmTask = async () => {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.requestWriteAccess) {
    tg.requestWriteAccess((granted: boolean) => {
      if (!granted) {
        window.showToast(t('rewards_pm_denied'), 'error');
        return;
      }
      // Mark: granted in THIS session
      safeStorage.set('pg_pm_granted', '1');
      safeSession.set('pg_pm_this_session', '1');
      window.renderApp();
    });
  } else {
    // Non-TG environment (dev): go straight to claim
    await _doPmClaim();
  }
};

// Called when user is in "ready to claim" state (new session after reopen)
(window as any).claimPmReward = async () => {
  await _doPmClaim();
};

// Close the Telegram Mini App so user can reopen (triggering new auth + allows_write_to_pm update)
(window as any).closeTgApp = () => {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.close) {
    tg.close();
  } else {
    window.showToast(t('rewards_pm_retry'), 'info' as any);
  }
};

async function _doPmClaim() {
  try {
    const res = await api.claimTask('allow_pm');
    if (res.ok) {
      const added = res.coins_added ?? 30;
      safeStorage.set('pg_pm_claimed', '1');
      safeStorage.remove('pg_pm_granted');
      safeSession.remove('pg_pm_this_session');
      updateCoins((state.user?.coins ?? 0) + added, true);
      window.showToast(t('rewards_task_claimed').replace('{n}', String(added)), 'success');
      window.renderApp();
    } else {
      // Backend still has allows_write_to_pm=0 — user needs one more reopen
      safeStorage.set('pg_pm_granted', '1');
      safeSession.set('pg_pm_this_session', '1'); // force "reopen" card again
      window.showToast(t('rewards_pm_retry'), 'info');
      window.renderApp();
    }
  } catch {
    window.showToast(t('rewards_task_error'), 'error');
  }
}

// ── Helpers ───────────────────────────────────────────────────────────

let _forceInviteRefresh = true;
let _referralLoadPromise: Promise<void> | null = null;

window.__markInviteNeedsRefresh = () => {
  _forceInviteRefresh = true;
};

async function ensureReferralLoaded(forceRefresh = false): Promise<void> {
  if (isHalted()) return;  // terminal auth state — no fetches
  if (!forceRefresh && state.referralStats) return;
  if (_referralLoadPromise) return _referralLoadPromise;

  _referralLoadPromise = (async () => {
    try {
      state.referralStats = await api.getReferral();
    } catch (e) {
      console.error('Referral load failed', e);
    } finally {
      _referralLoadPromise = null;
    }
  })();

  return _referralLoadPromise;
}


const MILESTONE_ICONS: Record<number, string> = { 1: '🤝', 3: '🌱', 10: '🎉', 25: '🌟' };

// ── Render ────────────────────────────────────────────────────────────

export function renderInvite(): string {
  if (_forceInviteRefresh) {
    _forceInviteRefresh = false;
    ensureReferralLoaded(true).then(() => { if (!isHalted()) window.renderApp(); });
  }

  const stats = state.referralStats;

  if (stats === null) {
    ensureReferralLoaded().then(() => { if (!isHalted()) window.renderApp(); });
    return `
      <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] z-10 scrollbar-none bg-void text-t-primary flex flex-col items-center justify-center">
        <div class="w-[48px] h-[48px] border-4 border-neon-cyan/20 border-t-neon-cyan rounded-full animate-spin"></div>
        <div class="mt-[16px] text-[14px] text-t-muted font-bold animate-pulse">${t('loading')}</div>
      </div>
    `;
  }

  // Quick UI state: localStorage is fast-path, server state is authoritative
  const emailClaimed   = safeStorage.get('pg_email_claimed')   === '1' || !!stats.task_claims?.email;
  const channelClaimed = safeStorage.get('pg_channel_claimed') === '1' || !!stats.task_claims?.channel_sub;
  const channelVisited = safeStorage.get('pg_channel_visited') === '1';
  const pmClaimed      = safeStorage.get('pg_pm_claimed')      === '1' || !!stats.task_claims?.allow_pm;
  // PM intermediate states (sessionStorage resets on Telegram Mini App reopen)
  const pmGranted      = !pmClaimed && safeStorage.get('pg_pm_granted') === '1';
  const pmJustGranted  = pmGranted && safeSession.get('pg_pm_this_session') === '1';
  const pmReadyToClaim = pmGranted && !pmJustGranted; // new session → reopen happened

  // Keep localStorage in sync with server truth
  if (stats.task_claims?.email)       safeStorage.set('pg_email_claimed',   '1');
  if (stats.task_claims?.channel_sub) safeStorage.set('pg_channel_claimed', '1');
  if (stats.task_claims?.allow_pm) {
    safeStorage.set('pg_pm_claimed', '1');
    safeStorage.remove('pg_pm_granted');
    safeSession.remove('pg_pm_this_session');
  }

  const nextMilestone = stats.milestones.find(m => !m.claimed) || null;
  const progressMax = nextMilestone
    ? nextMilestone.count
    : (stats.milestones[stats.milestones.length - 1]?.count ?? stats.total_invited);
  const progressPercent = Math.min(100, (stats.total_invited / Math.max(1, progressMax)) * 100);
  const nextMilestoneText = getNextMilestoneText(stats.total_invited, stats.milestones);

  // ── Achievement Cards (horizontal scroll) ──

  const achievementCardsHtml = stats.milestones.map((m, idx) => {
    const isClaimed = m.claimed;
    const isNext = !isClaimed && (idx === 0 || stats.milestones[idx - 1].claimed);
    const icon = MILESTONE_ICONS[m.count] || '🎯';
    const title = t(`milestone_title_${m.count}`) || `${m.count}`;
    const progress = Math.min(stats.total_invited, m.count);
    const rewardLabel = t(`milestone_reward_${m.count}`);

    let cardClass: string;
    let iconBg: string;
    let rewardBg: string;
    let statusEl: string;

    if (isClaimed) {
      cardClass = 'bg-neon-green/[0.07] border-neon-green/[0.3] shadow-[0_0_14px_rgba(57,255,20,0.08)]';
      iconBg = 'bg-neon-green/[0.15]';
      rewardBg = 'bg-neon-green/[0.15] text-neon-green border border-neon-green/[0.3]';
      statusEl = `<div class="text-[13px] font-extrabold text-neon-green">✅</div>`;
    } else if (isNext) {
      cardClass = 'bg-neon-gold/[0.06] border-neon-gold/[0.35] shadow-[0_0_14px_rgba(255,215,0,0.08)]';
      iconBg = 'bg-neon-gold/[0.15]';
      rewardBg = 'bg-neon-gold/[0.15] text-neon-gold border border-neon-gold/[0.35]';
      statusEl = `<div class="text-[11px] font-extrabold text-neon-gold">${progress}/${m.count}</div>`;
    } else {
      cardClass = 'bg-white/[0.03] border-white/[0.06]';
      iconBg = 'bg-white/[0.06]';
      rewardBg = 'bg-white/[0.05] text-t-muted';
      statusEl = `<div class="text-[11px] font-bold text-t-muted">${progress}/${m.count} 🔒</div>`;
    }

    return `
      <div class="flex-shrink-0 w-[112px] rounded-[20px] border ${cardClass} p-[14px_10px] flex flex-col items-center gap-[6px] text-center">
        <div class="w-[46px] h-[46px] rounded-full ${iconBg} flex items-center justify-center text-[24px]">${icon}</div>
        <div class="text-[12px] font-extrabold text-t-primary leading-tight px-[2px]">${title}</div>
        ${statusEl}
        <div class="rounded-[8px] py-[3px] px-[7px] text-[10px] font-extrabold leading-tight ${rewardBg} mt-[auto]">${rewardLabel}</div>
      </div>
    `;
  }).join('');

  // ── Earn More cards ──

  function earnCard(claimed: boolean, opts: {
    icon: string;
    iconBg: string;
    title: string;
    coins: string;
    coinsColor: string;
    action: string;
    actionEl: string;
  }): string {
    if (claimed) {
      return `
        <div class="flex items-center gap-[14px] p-[14px_16px] bg-glass border border-b-glass rounded-[18px] opacity-60">
          <div class="w-[44px] h-[44px] rounded-[14px] bg-white/[0.06] flex items-center justify-center text-[22px] shrink-0">${opts.icon}</div>
          <div class="flex-1 min-w-0">
            <div class="text-[15px] font-extrabold text-t-muted leading-snug">${opts.title}</div>
            <div class="text-[12px] text-t-muted font-bold mt-[3px]">${opts.coins}</div>
          </div>
          <div class="flex items-center gap-[4px] bg-neon-green/[0.15] border border-neon-green/[0.3] rounded-[10px] py-[6px] px-[10px] shrink-0">
            <span class="text-[11px]">✅</span>
            <span class="text-[12px] font-extrabold text-neon-green">${t('rewards_done_short')}</span>
          </div>
        </div>
      `;
    }
    return `
      <div class="flex items-center gap-[14px] p-[14px_16px] bg-glass border border-b-glass rounded-[18px] cursor-pointer active:scale-[0.98] transition-transform" ${opts.action}>
        <div class="w-[44px] h-[44px] rounded-[14px] ${opts.iconBg} flex items-center justify-center text-[22px] shrink-0">${opts.icon}</div>
        <div class="flex-1 min-w-0">
          <div class="text-[15px] font-extrabold text-t-primary leading-snug">${opts.title}</div>
          <div class="text-[12px] ${opts.coinsColor} font-bold mt-[3px]">${opts.coins}</div>
        </div>
        ${opts.actionEl}
      </div>
    `;
  }

  const emailCoins = emailClaimed
    ? `+50 🪙 ${t('rewards_earned')}`
    : `+50 🪙 ${t('rewards_available')}`;

  const emailCardHtml = earnCard(emailClaimed, {
    icon: '📧',
    iconBg: 'bg-neon-cyan/[0.15]',
    title: t('rewards_email_title'),
    coins: emailCoins,
    coinsColor: 'text-neon-cyan',
    action: 'data-action="open-email-popup"',
    actionEl: `<div class="w-[32px] h-[32px] rounded-full bg-white/[0.06] flex items-center justify-center text-[16px] text-t-muted shrink-0">›</div>`,
  });

  const channelCardHtml = channelClaimed
    ? earnCard(true, {
        icon: '📢', iconBg: 'bg-white/[0.06]',
        title: t('rewards_channel_title'),
        coins: `+50 🪙 ${t('rewards_earned')}`,
        coinsColor: 'text-t-muted', action: '', actionEl: '',
      })
    : channelVisited
      ? `
        <div class="flex items-center gap-[14px] p-[14px_16px] bg-glass border border-neon-gold/[0.3] rounded-[18px]">
          <div class="w-[44px] h-[44px] rounded-[14px] bg-neon-gold/[0.15] flex items-center justify-center text-[22px] shrink-0">📢</div>
          <div class="flex-1 min-w-0">
            <div class="text-[15px] font-extrabold text-t-primary leading-snug">${t('rewards_channel_title')}</div>
            <div class="text-[12px] text-neon-gold font-bold mt-[3px]">+50 🪙 ${t('rewards_available')}</div>
          </div>
          <button class="py-[7px] px-[13px] bg-neon-gold/[0.2] border border-neon-gold/[0.4] rounded-[10px] text-[13px] font-extrabold text-neon-gold whitespace-nowrap shrink-0" data-action="claim-channel-reward">
            ${t('rewards_channel_claim')}
          </button>
        </div>
      `
      : earnCard(false, {
          icon: '📢',
          iconBg: 'bg-neon-pink/[0.15]',
          title: t('rewards_channel_title'),
          coins: `+50 🪙 ${t('rewards_available')}`,
          coinsColor: 'text-neon-pink',
          action: 'data-action="open-channel-link"',
          actionEl: `<div class="w-[32px] h-[32px] rounded-full bg-white/[0.06] flex items-center justify-center text-[16px] text-t-muted shrink-0">›</div>`,
        });

  // PM card: 4 visual states
  let pmCardHtml: string;
  if (pmClaimed) {
    // State 4: Claimed — greyed out Done
    pmCardHtml = earnCard(true, {
      icon: '🔔', iconBg: 'bg-white/[0.06]',
      title: t('rewards_pm_title'),
      coins: `+30 🪙 ${t('rewards_earned')}`,
      coinsColor: 'text-t-muted', action: '', actionEl: '',
    });
  } else if (pmJustGranted) {
    // State 2: Just granted THIS session → tell user to close & reopen
    pmCardHtml = `
      <div class="flex flex-col gap-[10px] p-[14px_16px] bg-neon-gold/[0.07] border border-neon-gold/[0.4] rounded-[18px]">
        <div class="flex items-center gap-[14px]">
          <div class="w-[44px] h-[44px] rounded-[14px] bg-neon-gold/[0.2] flex items-center justify-center text-[22px] shrink-0">🔔</div>
          <div class="flex-1 min-w-0">
            <div class="text-[15px] font-extrabold text-neon-gold leading-snug">${t('rewards_pm_granted_title')}</div>
            <div class="text-[12px] text-neon-gold/70 font-semibold mt-[3px]">${t('rewards_pm_granted_sub')}</div>
          </div>
        </div>
        <button
          class="w-full py-[12px] bg-neon-gold/[0.2] border border-neon-gold/[0.4] rounded-[12px] text-[14px] font-extrabold text-neon-gold"
          data-action="close-tg-app"
        >${t('rewards_pm_close_btn')}</button>
      </div>
    `;
  } else if (pmReadyToClaim) {
    // State 3: New session after reopen → ready to claim
    pmCardHtml = `
      <div class="flex flex-col gap-[10px] p-[14px_16px] bg-neon-green/[0.07] border border-neon-green/[0.3] rounded-[18px]">
        <div class="flex items-center gap-[14px]">
          <div class="w-[44px] h-[44px] rounded-[14px] bg-neon-green/[0.2] flex items-center justify-center text-[22px] shrink-0">🔔</div>
          <div class="flex-1 min-w-0">
            <div class="text-[15px] font-extrabold text-neon-green leading-snug">${t('rewards_pm_ready_title')}</div>
            <div class="text-[12px] text-neon-green/70 font-semibold mt-[3px]">${t('rewards_pm_ready_sub')}</div>
          </div>
        </div>
        <button
          class="w-full py-[12px] bg-grad-blue shadow-[0_0_16px_rgba(45,107,255,0.35)] rounded-[12px] text-[14px] font-extrabold text-white"
          data-action="claim-pm-reward"
        >${t('rewards_pm_claim_btn')}</button>
      </div>
    `;
  } else {
    // State 1: Default — not yet interacted
    pmCardHtml = earnCard(false, {
      icon: '🔔',
      iconBg: 'bg-neon-gold/[0.15]',
      title: t('rewards_pm_title'),
      coins: `+30 🪙 ${t('rewards_available')}`,
      coinsColor: 'text-neon-gold',
      action: 'data-action="claim-pm-task"',
      actionEl: `<div class="w-[32px] h-[32px] rounded-full bg-white/[0.06] flex items-center justify-center text-[16px] text-t-muted shrink-0">›</div>`,
    });
  }

  // ── Full page render ──

  return `
    <div class="absolute inset-0 overflow-y-auto overflow-x-hidden pb-[96px] z-10 scrollbar-none bg-void text-t-primary">

      <!-- Header -->
      <div class="flex items-center justify-between p-[16px_20px_8px] relative z-[2]">
        <div class="font-display text-[22px] font-extrabold bg-grad-primary bg-clip-text text-transparent">${t('rewards_hub_title')}</div>
        <div class="flex items-center gap-[6px]">
          ${CoinChip({ amount: state.user?.coins ?? 0, onClick: "nav-premium" })}
          ${isPremiumActive() ? `
            <div class="flex items-center gap-[4px] bg-neon-gold/[0.15] border border-neon-gold/[0.3] rounded-pill py-[5px] px-[10px]">
              <span class="text-[13px]">👑</span>
              <span class="text-[11px] font-extrabold text-neon-gold">PRO</span>
            </div>
          ` : ''}
        </div>
      </div>

      <!-- Achievements Section (horizontal scroll) -->
      <div class="text-[11px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[10px] px-[20px] mt-[8px]">${t('rewards_achievements_label')}</div>
      <div class="flex gap-[10px] px-[16px] overflow-x-auto scrollbar-none pb-[4px]">
        ${achievementCardsHtml}
        <div class="flex-shrink-0 w-[4px]"></div>
      </div>

      <!-- Invite Friends Block -->
      <div class="mx-[16px] mt-[16px] p-[20px] bg-gradient-to-br from-[#2D6BFF]/[0.15] to-[#00E5FF]/[0.05] border border-[#2D6BFF]/[0.3] rounded-[24px]">
        <div class="flex items-end gap-[6px] mb-[4px]">
          <div class="font-display text-[44px] font-extrabold text-white leading-none">${stats.total_invited}</div>
          <div class="mb-[8px] text-[20px] text-white/30">/${progressMax}</div>
        </div>
        <div class="text-[13px] text-t-secondary font-semibold mb-[12px]">${t('invite_friends_count')}</div>
        <div class="h-[8px] bg-white/[0.08] rounded-pill overflow-hidden mb-[10px]">
          <div class="h-full rounded-pill bg-grad-blue shadow-[0_0_10px_rgba(45,107,255,0.4)] transition-all duration-500" style="width:${progressPercent}%"></div>
        </div>
        <div class="text-[13px] font-bold text-neon-cyan mb-[16px]">${nextMilestoneText}</div>
        <button
          class="flex items-center justify-center gap-[8px] w-full py-[16px] px-[24px] bg-grad-blue shadow-[0_0_20px_rgba(45,107,255,0.5)] rounded-pill text-white font-body text-[17px] font-extrabold"
          data-action="share-referral"
        >
          ${t('invite_btn')}
        </button>
        <!-- Prominent coins-per-friend info -->
        <div class="flex items-center justify-center gap-[6px] mt-[12px] p-[8px_16px] bg-neon-gold/[0.1] border border-neon-gold/[0.2] rounded-[12px]">
          <span class="text-[16px]">🪙</span>
          <span class="text-[13px] font-extrabold text-neon-gold">${t('rewards_per_friend_coins')}</span>
        </div>
      </div>

      <!-- Tasks Section -->
      <div class="text-[11px] font-extrabold tracking-[1.5px] uppercase text-t-muted mb-[10px] px-[20px] mt-[24px]">${t('rewards_tasks_label')}</div>
      <div class="flex flex-col gap-[8px] px-[16px]">
        ${emailCardHtml}
        ${channelCardHtml}
        ${pmCardHtml}
      </div>

      <!-- Referral Link -->
      <div class="m-[20px_16px_0] p-[16px] bg-glass border border-b-glass rounded-[18px]">
        <div class="text-[11px] font-extrabold text-t-muted tracking-[1px] mb-[10px]">${t('invite_link_label')}</div>
        <div class="flex items-center gap-[10px]">
          <div class="flex-1 text-[12px] text-neon-cyan font-bold bg-[#2D6BFF]/10 p-[10px_12px] rounded-[12px] border border-[#2D6BFF]/[0.2] truncate">
            ${stats.referral_link}
          </div>
          <div class="p-[10px_14px] bg-grad-primary rounded-[12px] text-[12px] font-extrabold cursor-pointer whitespace-nowrap" data-action="copy-referral-link">
            ${t('invite_copy')}
          </div>
        </div>
      </div>

      <div class="h-[24px]"></div>
    </div>
  `;
}
